import { z } from 'zod';

import {
	type CurrencyCode,
	currencyCodeSchema,
	dateTimeSchema,
} from '@apple/utils/globalization/models';
import { paginatedListDtoSchema } from '@apple/utils/pagination/models';

import { type InventoryStatus, inventoryStatusSchema } from '@apple/features/product/models/shared';

import type { SortedImage } from './image';
import { sortedImageSchema } from './image';

export type ProductKey = z.infer<typeof productKeySchema>;
export const productKeySchema = z.string();

/** @see Manzanita.Web.Models.ProductModel */
export interface ProductModel {
	itemId: ProductKey;
	title: string;
	sku: string;
	unitOfMeasure: string;
	/** Null when the product matches multiple warehouses and has different states per warehouse */
	itemState: string | null;
	states: InventoryStatus[];
	description: string | null;
	images: SortedImage[];
	externalUrl?: string | null;
}

/** @see Manzanita.Web.Models.ProductModel */
export const productModelSchema = z.object({
	itemId: productKeySchema,
	title: z.string(),
	sku: z.string(),
	unitOfMeasure: z.string(),
	/** Null when the product matches multiple warehouses and has different states per warehouse */
	itemState: z.string().nullable(),
	states: inventoryStatusSchema.array(),
	description: z.string().nullable(),
	images: z.array(sortedImageSchema),
	externalUrl: z.string().optional().nullable(),
}) satisfies z.ZodType<ProductModel>;

/** @see Manzanita.Web.Models.ProductListingModel */
export interface ProductListingModel extends ProductModel {
	displayString: string;
	requiresApproval: boolean;
	beginDate?: string | Date | null;
	endDate?: string | Date | null;
	deleted: boolean;
	showInventory: boolean;
	administratorOnly: boolean;
	showInventoryStatus: boolean;
	updatedDate?: string | Date | null;
	quantityLimit?: number | null;
	isBackorderable: boolean;
	/** Null when the product matches multiple warehouses and has different states per warehouse */
	estimatedInStockDate?: string | null;
	/** Null when the product matches multiple warehouses and has different states per warehouse */
	unitPrice: string | null;
	/** Null when the product matches multiple warehouses and has different states per warehouse */
	numericUnitPrice: number | null;
	/** Null when the product matches multiple warehouses and has different states per warehouse */
	currencyCode: CurrencyCode | null;
	isFeatured: boolean;
}

/** @see Manzanita.Web.Models.ProductListingModel */
export const productListingModelSchema = productModelSchema.extend({
	displayString: z.string(),
	requiresApproval: z.boolean(),
	beginDate: dateTimeSchema.nullable(),
	endDate: dateTimeSchema.nullable(),
	deleted: z.boolean(),
	showInventory: z.boolean(),
	administratorOnly: z.boolean(),
	showInventoryStatus: z.boolean(),
	updatedDate: dateTimeSchema.nullable(),
	quantityLimit: z.number().nullable(),
	isBackorderable: z.boolean(),
	/** Null when the product matches multiple warehouses and has different states per warehouse */
	estimatedInStockDate: z.string().nullable(),
	/** Null when the product matches multiple warehouses and has different unit prices per warehouse */
	unitPrice: z.string().nullable(),
	/** Null when the product matches multiple warehouses and has different unit prices per warehouse */
	numericUnitPrice: z.number().nullable(),
	/** Null when the product matches multiple warehouses and has different unit prices per warehouse */
	currencyCode: currencyCodeSchema.nullable(),
	isFeatured: z.boolean(),
}) satisfies z.ZodType<ProductListingModel>;

/** Manzanita.Collections.PaginatedList<ProductListingModel> */
export const productsPaginatedListSchema = paginatedListDtoSchema(productListingModelSchema);

/** @see Manzanita.Web.ApiControllers.AutoCompleteApiController */
export interface ProductAutocompleteRequest {
	searchText: string;
	partNumbers: string[];
	customerCode: string;
}

/** @see Manzanita.Web.ApiControllers.AutoCompleteApiController */
export const productAutocompleteRequestSchema = z.object({
	searchText: z.string(),
	partNumbers: z.array(z.string()),
	customerCode: z.string(),
}) satisfies z.ZodType<ProductAutocompleteRequest>;
